import {
  Avatar,
  Badge,
  EmptyState,
  Menu,
  type MenuItem,
  Pagination,
  Skeleton,
  Table,
  type TableColumn,
  When,
  createPagedList,
  useConfirm,
} from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { useNavigate } from '@solidjs/router';
import type { UserListItem } from '@uchat-team-management/shared';
import { For, Show } from 'solid-js';
import { type UsersFilters, useDeleteUser, useUpdateUser, useUsersList } from './hooks';

interface Props {
  filters: () => UsersFilters;
}

export const UsersTable = (props: Props) => {
  const query = useUsersList(props.filters);
  const { rows, page, totalPages, jumping, goToPage } = createPagedList(query, props.filters);
  const navigate = useNavigate();

  const columns = (): readonly TableColumn<UserListItem>[] => [
    {
      key: 'name',
      header: 'Name',
      render: (row) => (
        <div class="flex items-center gap-3">
          <Avatar user={row} class="h-8 w-8 shrink-0" />
          <div class="flex min-w-0 flex-col">
            <span class="font-medium">{row.displayName || '—'}</span>
            <span class="text-xs text-neutral-500">{row.email}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'github',
      header: 'GitHub',
      render: (row) =>
        row.githubUsername ? <span class="text-neutral-500">{row.githubUsername}</span> : '—',
    },
    {
      key: 'roles',
      header: 'Roles',
      render: (row) => (
        <div class="flex flex-wrap gap-1">
          <For each={row.roles}>{(role) => <Badge tone="neutral">{role.name}</Badge>}</For>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge
          tone={
            row.status === 'active' ? 'success' : row.status === 'disabled' ? 'danger' : 'warning'
          }
        >
          {row.status === 'active' ? 'Active' : row.status === 'disabled' ? 'Disabled' : 'Pending'}
        </Badge>
      ),
    },
    {
      key: 'lastSeen',
      header: 'Last seen',
      render: (row) => (row.lastSeenAt ? <When date={row.lastSeenAt} class="" /> : '—'),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (row) => <RowMenu row={row} onEdit={() => navigate(`/admin/users/${row.id}`)} />,
    },
  ];

  return (
    <Show when={!query.isPending} fallback={<Skeleton class="h-40" />}>
      <Show
        when={query.data}
        fallback={<EmptyState variant="error" title="Failed to load users" />}
      >
        <Table
          data={rows()}
          columns={columns()}
          rowKey={(r) => r.id}
          loading={jumping()}
          empty={<EmptyState title="No users yet" body="Invite someone to get started." />}
        />
        <Pagination
          class="mt-4"
          page={page()}
          totalPages={totalPages()}
          loading={jumping()}
          onPage={goToPage}
        />
      </Show>
    </Show>
  );
};

const RowMenu = (props: { row: UserListItem; onEdit: () => void }) => {
  const { session } = useAuth();
  const remove = useDeleteUser(props.row.id);
  const update = useUpdateUser(props.row.id);
  const confirmDialog = useConfirm();
  const canDisable = () => session()?.permissions.has('users:disable') ?? false;
  const isDisabled = () => props.row.status === 'disabled';

  const items = (): MenuItem[] => {
    const list: MenuItem[] = [{ label: 'Edit', onSelect: props.onEdit }];
    if (canDisable()) {
      list.push({
        label: isDisabled() ? 'Enable' : 'Disable',
        onSelect: async () => {
          const ok = await confirmDialog(
            isDisabled()
              ? {
                  title: `Enable ${props.row.email}?`,
                  body: 'They will be able to sign in and use the app again.',
                  confirmLabel: 'Enable',
                }
              : {
                  title: `Disable ${props.row.email}?`,
                  body: 'They will be signed out and blocked from the app until re-enabled.',
                  confirmLabel: 'Disable',
                  danger: true,
                },
          );
          if (ok) update.mutate({ disabled: !isDisabled() });
        },
      });
    }
    list.push({
      label: 'Remove',
      danger: true,
      onSelect: async () => {
        const ok = await confirmDialog({
          title: `Remove ${props.row.email}?`,
          body: 'Their devices will be unassigned.',
          confirmLabel: 'Remove',
          danger: true,
        });
        if (ok) remove.mutate(undefined as never);
      },
    });
    return list;
  };

  return <Menu items={items()} />;
};
